import DataTable from 'Components/DataTable';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { isi, Task_Progress } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { omitControlProps } from 'Utility/documentHandling';
import { useMyServiceContext } from '../..';
import colors from '../../../../../config/theme/colors';
import { arrayOfAll } from 'neuro-utils';
import browserLanguage from 'Utility/browserLanguage';
import { useAppSelector } from 'Store/index';

const isiFields: Array<keyof IISI> = arrayOfAll.keys<SafeOmit<IISI, 'date'>>()([
  'fallingAsleepDifficulty',
  'stayingAsleepDifficulty',
  'wakingUpTooEarly',
  'currentSleepSatisfaction',
  'interferingDailyFunction',
  'noticeableToOthers',
  'worriedAboutSleepProblem',
]);

export const Isi = (): React.JSX.Element => {
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const { editing, setEditingData, viewing, setViewingObj, setEditingObj } = useMyServiceContext<'isi'>();
  const isiData = editing?.data ?? viewing?.data ?? {};
  const isiLocales = isi.localizations;
  const onChangeSurveyForm = (values: TOnChangeValues): void => {
    const field = Object.keys(values)[0];
    if (!field) return;
    const value = field === 'date' ? Object.values(values)[0] : parseInt(`${Object.values(values)[0]}`);
    setEditingData?.({
      ...isiData,
      [field]: value,
    });
  };

  return (
    <>
      {!viewing ? (
        <div style={{ width: '80%', color: colors.tertiaryText, marginBottom: '2rem' }}>
          <p>{isiLocales[locale]['surveyInfo']}</p>
          <p>{isiLocales[locale]['surveyFillingInfo']}</p>
        </div>
      ) : null}
      <BlockWrapper
        title={viewing ? formatPartialDate(isiData.date) : undefined}
        buttons={
          viewing
            ? [
                {
                  title: 'general.edit',
                  onClick: () => {
                    setViewingObj(null);
                    setEditingObj({ type: 'isi', data: isiData });
                  },
                },
              ]
            : undefined
        }
      >
        {!viewing && (
          <FormRow title={'general.date'} headerWidth={5.5}>
            <InputHandler
              name="date"
              type="PartialDate"
              dateDefault="now"
              formData={{
                document: { date: isiData.date },
                onChange: onChangeSurveyForm,
              }}
              editing={!!editing}
            />
          </FormRow>
        )}
        <div style={{ width: '60%', marginBottom: '2rem', marginTop: viewing ? '6rem' : undefined }}>
          <DataTable
            headers={viewing ? ['', '', ''] : ['', '']}
            data={{
              rowData: isiFields.map(f => {
                const rowSet = [
                  isiLocales[locale][f] ?? '-',
                  <div key={f} style={viewing ? { width: '15rem' } : undefined}>
                    <InputHandler
                      name={f}
                      type="Radio"
                      editing={!!editing}
                      formData={{
                        document: { [f]: isiData[f] },
                        onChange: onChangeSurveyForm,
                      }}
                      options={[0, 1, 2, 3, 4]}
                      optionFormatter={o => isiLocales[locale][`opts.${f}.${o}`] ?? '-'}
                    />
                  </div>,
                ];
                if (viewing && f !== 'date') {
                  rowSet.push(
                    <div style={{ fontWeight: 600, width: '3rem', display: 'flex', justifyContent: 'center' }}>
                      {isiData[f] || isiData[f] === 0 ? `${isiData[f]}` : ''}
                    </div>,
                  );
                }
                return rowSet;
              }),
              rowStyle: viewing ? undefined : isiFields.map(() => ({ rowAlign: 'top' })),
            }}
          />
        </div>
        <FormRow
          title={isiLocales[locale]['score']}
          headerStyle={{ fontWeight: 600 }}
          headerWidth={viewing ? 6.75 : 5.5}
          formatTitle={false}
        >
          <div style={{ color: colors.tertiaryText, fontWeight: 600 }}>
            <span>
              {typeof Task_Progress.calculateProgress('isi', omitControlProps(isiData)).yielded === 'number'
                ? (Task_Progress.calculateProgress('isi', omitControlProps(isiData)).yielded as number)
                : '-'}
            </span>
            <span>{` / ${Task_Progress.calculateProgress('isi', omitControlProps(isiData)).ceiling}`}</span>
          </div>
        </FormRow>
        <div style={{ color: colors.tertiaryText, marginTop: '-4rem', display: 'flex' }}>
          <div style={{ display: 'flex', flexDirection: 'column', marginRight: '2rem' }}>
            <span>{'0-7'}</span>
            <span>{'8-14'}</span>
            <span>{'15-21'}</span>
            <span>{'22-28'}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{isiLocales[locale]['opts.interpretation.noInsomnia']}</span>
            <span>{isiLocales[locale]['opts.interpretation.subTreshold']}</span>
            <span>{isiLocales[locale]['opts.interpretation.moderate']}</span>
            <span>{isiLocales[locale]['opts.interpretation.severe']}</span>
          </div>
        </div>
      </BlockWrapper>
      <div style={{ color: colors.tertiaryText, fontStyle: 'italic' }}>
        <p>{isiLocales[locale]['reference']}</p>
      </div>
    </>
  );
};
